"use client";

import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _extends from "@babel/runtime/helpers/esm/extends";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Dialog, DialogDescription, DialogDismiss, DialogHeading, Heading, HeadingLevel, useDialogStore, useStoreState } from "@ariakit/react";
import { RemoveScroll } from "react-remove-scroll";
import { iconSize, shadows, spacing, textStyle } from "../../dimensions";
import SquareButton from "../SquareButton";
import { CrossIcon, ArrowIcon } from "../../icons";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { SidePanelContext, useSidePanelContext } from "./SidePanelContext";
import { usePortalElement } from "../../hooks/usePortalElement";
var SidePanel = function SidePanel(_ref) {
  var _ref$isOpen = _ref.isOpen,
    isOpen = _ref$isOpen === void 0 ? false : _ref$isOpen,
    onClose = _ref.onClose,
    _ref$placement = _ref.placement,
    placement = _ref$placement === void 0 ? "right" : _ref$placement,
    children = _ref.children,
    _ref$isContained = _ref.isContained,
    isContained = _ref$isContained === void 0 ? false : _ref$isContained,
    mobileMaxViewportWidth = _ref.mobileMaxViewportWidth,
    ariaLabel = _ref.ariaLabel,
    className = _ref.className;
  var isMobile = useMediaQuery("(max-width : ".concat(mobileMaxViewportWidth, ")"));
  var dialogStore = useDialogStore({
    open: isOpen,
    setOpen: function setOpen(open) {
      if (!open) {
        onClose();
      }
    }
  });
  var contextValue = useMemo(function () {
    return {
      isContained: isContained
    };
  }, [isContained]);
  var mounted = useStoreState(dialogStore, "mounted");
  var portalElement = usePortalElement();
  if (!mounted) return null;
  return /*#__PURE__*/React.createElement(SidePanelContext.Provider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement(RemoveScroll, {
    forwardProps: true,
    allowPinchZoom: true,
    enabled: isMobile || !isContained ? isOpen : false
  }, /*#__PURE__*/React.createElement(StyledSidePanel, _extends({
    store: dialogStore,
    portalElement: portalElement,
    backdrop: isContained ? false : Overlay,
    modal: isMobile || !isContained,
    portal: isMobile || !isContained
    // The implementation of AriKit for body scroll lock doesn't work for iCCP since it resets window scroll on iOS, temporary use a custom solution until fixed on their side
    ,
    preventBodyScroll: false,
    "aria-modal": true,
    className: className,
    $placement: placement,
    $isContained: isContained,
    $mobileMaxViewportWidth: mobileMaxViewportWidth
    // aria-labelledby does not work reliably in Chrome/Firefox w VoiceOver, so we use aria-label instead (GDS-278)
    ,
    "aria-label": ariaLabel
  }, ariaLabel ? {
    "aria-labelledby": undefined
  } : {}), children)));
};
var Header = function Header(_ref2) {
  var closeButtonLabel = _ref2.closeButtonLabel,
    backButtonLabel = _ref2.backButtonLabel,
    children = _ref2.children;
  return /*#__PURE__*/React.createElement(StyledHeader, null, backButtonLabel ? /*#__PURE__*/React.createElement(BackButton, {
    label: backButtonLabel
  }) : null, /*#__PURE__*/React.createElement(StyledTitleContainer, {
    $padLeft: !!backButtonLabel,
    $padRight: !!closeButtonLabel
  }, children), closeButtonLabel ? /*#__PURE__*/React.createElement(CloseButton, {
    label: closeButtonLabel
  }) : null);
};
var Title = function Title(_ref3) {
  var level = _ref3.level,
    children = _ref3.children;
  var _useSidePanelContext = useSidePanelContext(),
    isContained = _useSidePanelContext.isContained;
  return /*#__PURE__*/React.createElement(HeadingLevel, {
    level: isContained ? level || 2 : 1
  }, /*#__PURE__*/React.createElement(StyledTitle, null, children));
};
var Subtitle = function Subtitle(_ref4) {
  var level = _ref4.level,
    children = _ref4.children;
  var _useSidePanelContext2 = useSidePanelContext(),
    isContained = _useSidePanelContext2.isContained;
  return /*#__PURE__*/React.createElement(HeadingLevel, {
    level: isContained ? level || 3 : 2
  }, /*#__PURE__*/React.createElement(StyledSubtitle, null, children));
};
var CloseButton = function CloseButton(_ref5) {
  var label = _ref5.label;
  return /*#__PURE__*/React.createElement(DialogDismiss, {
    render: function render(_ref6) {
      var onClick = _ref6.onClick;
      return /*#__PURE__*/React.createElement(StyledIconButton, {
        variant: "ghost",
        label: label,
        onClick: onClick,
        icon: /*#__PURE__*/React.createElement(CrossIcon, {
          size: "1em"
        }),
        $placement: "right"
      });
    }
  });
};
var BackButton = function BackButton(_ref7) {
  var label = _ref7.label;
  return /*#__PURE__*/React.createElement(DialogDismiss, {
    render: function render(_ref8) {
      var onClick = _ref8.onClick;
      return /*#__PURE__*/React.createElement(StyledIconButton, {
        variant: "ghost",
        label: label,
        onClick: onClick,
        icon: /*#__PURE__*/React.createElement(ArrowIcon, {
          size: "1em"
        }),
        $placement: "left"
      });
    }
  });
};
var TopWideContainer = function TopWideContainer(_ref9) {
  var className = _ref9.className,
    _ref9$sticky = _ref9.sticky,
    sticky = _ref9$sticky === void 0 ? false : _ref9$sticky,
    children = _ref9.children;
  return /*#__PURE__*/React.createElement(StyledTopWideContainer, {
    className: className,
    $sticky: sticky
  }, children);
};
var Content = function Content(_ref10) {
  var _ref10$hasPadding = _ref10.hasPadding,
    hasPadding = _ref10$hasPadding === void 0 ? true : _ref10$hasPadding,
    children = _ref10.children,
    labelId = _ref10.labelId;
  var ref = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasScroll = _useState2[0],
    setHasScroll = _useState2[1];
  useEffect(function () {
    var tag = ref.current;
    if (!tag) return;
    var resizeObserver = new ResizeObserver(function () {
      var overflowY = getComputedStyle(tag, null).overflowY;
      if (tag.scrollHeight > tag.clientHeight && (overflowY === "auto" || overflowY === "scroll")) {
        setHasScroll(true);
      } else {
        setHasScroll(false);
      }
    });
    resizeObserver.observe(tag);
    return function () {
      return resizeObserver.disconnect();
    };
  }, [setHasScroll]);
  return /*#__PURE__*/React.createElement(StyledContent, {
    $hasPadding: hasPadding,
    ref: ref,
    tabIndex: hasScroll ? 0 : -1,
    "aria-labelledby": labelId
  }, children);
};
var StyledSidePanel = styled(Dialog).withConfig({
  displayName: "SidePanel__StyledSidePanel",
  componentId: "sc-1vmo9z7-0"
})(["--gds-side-panel-padding-size:", ";--gds-side-panel-border-color:", ";position:fixed;inset:0;display:grid;grid-template-rows:auto 1fr auto;background-color:", ";box-shadow:", ";transform:translateX( ", " );transition:transform 400ms cubic-bezier(0.16,1,0.3,1);", "{position:", ";width:", ";", ";}&[data-enter]{transform:translateX(0);}"], spacing.theatre, function (props) {
  return props.theme.base.borderMidtone;
}, function (props) {
  return props.theme.colors.base.bg;
}, shadows.dusk, function (props) {
  return props.$placement === "left" ? "-100%" : "100%";
}, function (props) {
  return "@media not all and (max-width: ".concat(props.$mobileMaxViewportWidth, ")");
}, function (props) {
  return props.$isContained && "absolute";
}, function (props) {
  return props.$isContained ? "100%" : "420px";
}, function (props) {
  return props.$placement === "left" ? css(["right:auto;left:0;"]) : css(["right:0;left:auto;"]);
});
var StyledHeader = styled.header.withConfig({
  displayName: "SidePanel__StyledHeader",
  componentId: "sc-1vmo9z7-1"
})(["--gds-side-panel-icon-size:32px;position:relative;padding:var(--gds-side-panel-padding-size);border-bottom:1px solid var(--gds-side-panel-border-color);"]);
var StyledIconButton = styled(SquareButton).withConfig({
  displayName: "SidePanel__StyledIconButton",
  componentId: "sc-1vmo9z7-2"
})(["position:absolute;top:var(--gds-side-panel-padding-size);width:var(--gds-side-panel-icon-size);height:var(--gds-side-panel-icon-size);", " &::after{position:absolute;inset:calc((", " - var(--gds-side-panel-icon-size)) / 2 * -1);width:", ";height:", ";content:\"\";}"], function (props) {
  return props.$placement === "left" ? css(["left:var(--gds-side-panel-padding-size);"]) : css(["right:var(--gds-side-panel-padding-size);"]);
}, iconSize.total, iconSize.total, iconSize.total);
var StyledTitleContainer = styled.div.withConfig({
  displayName: "SidePanel__StyledTitleContainer",
  componentId: "sc-1vmo9z7-3"
})(["display:flex;flex-direction:column;gap:", ";justify-content:center;min-height:var(--gds-side-panel-icon-size);padding-right:", ";padding-left:", ";"], spacing.lounge, function (props) {
  return props.$padRight ? "calc(var(--gds-side-panel-icon-size) + ".concat(spacing.hall, ")") : 0;
}, function (props) {
  return props.$padLeft ? "calc(var(--gds-side-panel-icon-size) + ".concat(spacing.hall, ")") : 0;
});
var StyledTitle = styled(DialogHeading).withConfig({
  displayName: "SidePanel__StyledTitle",
  componentId: "sc-1vmo9z7-4"
})(["position:relative;", " margin:0;"], textStyle.fiji);
var StyledSubtitle = styled(Heading).withConfig({
  displayName: "SidePanel__StyledSubtitle",
  componentId: "sc-1vmo9z7-5"
})(["", " margin:0;color:", ";"], textStyle.etna, function (props) {
  return props.theme.text.secondary;
});
var StyledDescription = styled(DialogDescription).withConfig({
  displayName: "SidePanel__StyledDescription",
  componentId: "sc-1vmo9z7-6"
})(["", " margin:0;color:", ";"], textStyle.rainier, function (props) {
  return props.theme.colors.text.primary;
});
var StyledContent = styled.div.withConfig({
  displayName: "SidePanel__StyledContent",
  componentId: "sc-1vmo9z7-7"
})(["", " overflow-y:auto;"], function (_ref11) {
  var $hasPadding = _ref11.$hasPadding;
  return $hasPadding && css(["padding:var(--gds-side-panel-padding-size);"]);
});
var StyledTopWideContainer = styled.div.withConfig({
  displayName: "SidePanel__StyledTopWideContainer",
  componentId: "sc-1vmo9z7-8"
})(["", " margin-top:calc(var(--gds-side-panel-padding-size) * -1);margin-right:calc(var(--gds-side-panel-padding-size) * -1);margin-bottom:", ";margin-left:calc(var(--gds-side-panel-padding-size) * -1);padding:", " var(--gds-side-panel-padding-size);"], function (props) {
  return props.$sticky && css(["position:sticky;top:calc(var(--gds-side-panel-padding-size) * -1);"]);
}, spacing.amphitheatre, spacing.amphitheatre);
var StyledFooter = styled.div.withConfig({
  displayName: "SidePanel__StyledFooter",
  componentId: "sc-1vmo9z7-9"
})(["padding:var(--gds-side-panel-padding-size);border-top:1px solid var(--gds-side-panel-border-color);"]);
var Overlay = styled.div.withConfig({
  displayName: "SidePanel__Overlay",
  componentId: "sc-1vmo9z7-10"
})(["position:fixed;inset:0;background-color:", ";&[data-enter]{opacity:1;transition:opacity 400ms cubic-bezier(0.16,1,0.3,1);}&[data-leave]{opacity:0;}"], function (props) {
  return props.theme.colors.base.overlay;
});
SidePanel.Header = Header;
SidePanel.Title = Title;
SidePanel.Subtitle = Subtitle;
SidePanel.Content = Content;
SidePanel.TopWideContainer = TopWideContainer;
SidePanel.Description = StyledDescription;
SidePanel.Footer = StyledFooter;
export default SidePanel;